export type InterventionListItemResponse = {
  id: string;
  interventionNumber: string;
  interventionTypeId: string;
  interventionTypeName: string;
  ownerEmployeeId: string | null;
  ownerDependantId: string | null;
  status: string;
  startDate: string;
  isActive: boolean;
  // A consent was declined: the case is flagged, not closed
  consentDeclined: boolean;
  // Who the case is for (name and surname) and the customer they belong to
  ownerName: string | null;
  customer: string | null;
  createdDate: string;
};

export type InterventionTypeResponse = {
  id: string;
  name: string;
  code: string;
  referralModel: 'EXPRESS' | 'SELECT';
  isActive: boolean;
  fcrAvailable: boolean;
  expectedOutcome: string | null;
  assessmentTypeCodes: string[];
  closingReportType: 'CloseOut' | 'Outcome' | null;
  usesSessionNotes: boolean;
  // Whether the type has treatment goals (set per type; off for Financial and Legal Assistance)
  usesTreatmentGoals: boolean;
};

// Who the intervention is for: an employee, a manager (also an employee), a dependant, or someone else
// (described in otherOwnerType). The owner itself is always an employee or a dependant.
export type InterventionOwnerType = 'EMPLOYEE' | 'MANAGER' | 'DEPENDANT' | 'OTHER';

// What the request says about the case; the New Request step creates it and the New request tab edits it.
type InterventionRequestFields = {
  interventionTypeId: string;
  interventionName: string | null;
  description: string | null;
  reason: string | null;
  // Where the request came from and what kind it is (New Request step); the lists below are items of reference lists
  sourceChannelId: string | null;
  requestTypeId: string | null;
  priorityId: string | null;
  riskClassificationId: string | null;
  startDate: string;
  targetReviewDate: string | null;
  ownerType: InterventionOwnerType | null;
  otherOwnerType: string | null;
  ownerEmployeeId: string | null;
  ownerDependantId: string | null;
  confidentialityLevelId: string | null;
  currentOwnerPractitionerId: string | null;
  // The user type of the member (an item of the member type list: Employee, Dependent, Manager, ...)
  userTypeId: string | null;
};

// A member who is not on the client's list, captured on the phone; the Eligibility step completes the rest.
export type NewMemberRequest = {
  clientCompanyId: string;
  firstName: string;
  surname: string;
  idNumber: string | null;
  emailAddress: string | null;
  contactNumber: string | null;
};

// Standalone Intervention creation (MW-149) - the server assigns InterventionNumber and always starts
// Status at 'New', so neither is supplied here. A new member is added with the request, so no existing owner is sent with it.
export type CreateInterventionRequest = InterventionRequestFields & {
  newMember: NewMemberRequest | null;
};

export type UpdateInterventionRequest = InterventionRequestFields & {
  interventionNumber: string;
  status: string;
  closureDate: string | null;
  closureReason: string | null;
  // Required when closing from service delivery onwards; ignored on any other save.
  closureOutcomeId: string | null;
};

export type InterventionResponse = UpdateInterventionRequest & {
  id: string;
  interventionTypeName: string;
  isActive: boolean;
  hasCia: boolean;
  closureOutcomeId: string | null;
  closureOutcomeName: string | null;
  // The names of the chosen list items, for display
  priority: string | null;
  riskClassification: string | null;
  confidentialityLevel: string | null;
  sourceChannel: string | null;
  requestType: string | null;
  userType: string | null;
  requestedAt: string;
  // Pending, Eligible or NotEligible, decided at the Eligibility step
  eligibilityStatus: string;
  // The finer status inside the stage (code, name and its category Open, Pending or Closed)
  subStatusCode: string | null;
  subStatus: string | null;
  subStatusCategory: string | null;
  usesTreatmentGoals: boolean;
  consentDeclined: boolean;
};

export type InterventionTransactionHistoryResponse = {
  id: string;
  interventionId: string;
  action: string;
  changedDate: string;
  changedBy: string | null;
  description: string | null;
};

export type AssignInterventionAffiliateRequest = {
  affiliateId: string;
};

export type InterventionAffiliateAssignmentResponse = {
  id: string;
  interventionId: string;
  affiliateId: string;
  assignmentStatus: string;
  assignedDate: string;
};

export type ProviderReferralAffiliateResponse = {
  id: string;
  affiliateId: string;
  affiliateName: string;
  affiliateSurname: string;
  isActive: boolean;
};

export type ProviderAssignmentResponse = {
  id: string;
  interventionId: string;
  affiliateId: string;
  providerReferralId: string | null;
  assignmentStatus: string;
  assignedDate: string;
  acceptedDate: string | null;
  declinedDate: string | null;
  declineReason: string | null;
  withdrawnDate: string | null;
  withdrawnReason: string | null;
};

export type ProviderReferralResponse = {
  id: string;
  interventionId: string;
  referralType: 'EXPRESS' | 'SELECT';
  referralDate: string;
  acceptanceStatus: string | null;
  affiliates: ProviderReferralAffiliateResponse[];
  assignments: ProviderAssignmentResponse[];
};

export type CreateProviderReferralRequest = {
  selectedAffiliateId: string | null;
};

export type DeclineReferralRequest = {
  declineReason: string;
};

export type WithdrawReferralRequest = {
  withdrawReason: string;
};

export type ReassignReferralRequest = {
  newAffiliateId: string;
};

export type InterventionTaskResponse = {
  id: string;
  interventionId: string;
  interventionTaskTemplateId: string;
  name: string;
  stage: string;
  displayOrder: number;
  isRequiredForTransition: boolean;
  status: 'Pending' | 'Completed' | 'Cancelled';
  completedDate: string | null;
  completedBy: string | null;
  notes: string | null;
  // The system event that completes the task (for example ReferralConsentGranted), or null when a person ticks it off.
  completesOnEvent: string | null;
};

export type InterventionSessionStatus = 'Scheduled' | 'Attended' | 'NoShow' | 'Cancelled';

// Dates are wall-clock times with no timezone ('2026-10-14T09:30:00'); they are shown exactly as entered.
export type InterventionSessionResponse = {
  id: string;
  interventionId: string;
  sessionNumber: number;
  affiliateId: string | null;
  affiliateName: string | null;
  sessionDate: string;
  durationMinutes: number | null;
  counsellingMediumId: string | null;
  counsellingMediumName: string | null;
  location: string | null;
  status: InterventionSessionStatus;
  checkInTime: string | null;
  checkOutTime: string | null;
  noShowReason: string | null;
  attendanceVerifiedDate: string | null;
  notes: string | null;
};

// affiliateId is only honoured for internal callers; an affiliate-portal caller always schedules as themselves.
export type InterventionSessionRequest = {
  affiliateId: string | null;
  sessionDate: string;
  durationMinutes: number | null;
  counsellingMediumId: string | null;
  location: string | null;
  notes: string | null;
};

export type InterventionReportType = 'CloseOut' | 'Session' | 'Outcome';

export type InterventionReportStatus = 'Submitted' | 'Approved' | 'Returned';

export type InterventionReportResponse = {
  id: string;
  interventionId: string;
  reportType: InterventionReportType;
  sessionId: string | null;
  sessionNumber: number | null;
  affiliateId: string | null;
  affiliateName: string | null;
  status: InterventionReportStatus;
  closureOutcomeId: string | null;
  closureOutcomeName: string | null;
  summary: string | null;
  goalsAchieved: string | null;
  recommendations: string | null;
  functioningAtClosure: string | null;
  submittedDate: string;
  reviewedDate: string | null;
  reviewNotes: string | null;
};

// A close-out report (the Case Closure Form) has a case status, goals achieved, recommendations and level of
// functioning; a session report names its session and has a summary; an outcome report has a summary and the
// same optional closing fields.
export type SubmitInterventionReportRequest = {
  reportType: InterventionReportType;
  sessionId: string | null;
  closureOutcomeId: string | null;
  summary: string | null;
  goalsAchieved: string | null;
  recommendations: string | null;
  functioningAtClosure: string | null;
  goalOutcomes?: TreatmentGoalOutcomeRequest[];
};

export type InterventionConsentType = 'Referral' | 'Service' | 'Disclosure';

// What the member decided. A declined consent only flags the case; it does not close it.
export type InterventionConsentDecision = 'Granted' | 'Declined';

// Active is the version in force; Declined is a refusal as the latest answer; Superseded is an earlier version a later answer replaced.
export type InterventionConsentStatus = 'Active' | 'Withdrawn' | 'Expired' | 'Declined' | 'Superseded';

export type InterventionConsentResponse = {
  id: string;
  interventionId: string;
  consentType: InterventionConsentType;
  version: number;
  status: InterventionConsentStatus;
  decision: InterventionConsentDecision;
  decidedDate: string;
  givenBy: string | null;
  scope: string | null;
  expiryDate: string | null;
  withdrawnDate: string | null;
  withdrawnReason: string | null;
  consentScriptItemId: string | null;
};

export type GrantInterventionConsentRequest = {
  consentType: InterventionConsentType;
  // Granted when left out
  decision?: InterventionConsentDecision;
  givenBy: string | null;
  scope: string | null;
  expiryDate: string | null;
  consentScriptItemId?: string | null;
};

// Either the member attended (check-in and check-out are optional) or did not (a reason is required).
export type InterventionSessionAttendanceRequest = {
  attended: boolean;
  checkInTime: string | null;
  checkOutTime: string | null;
  noShowReason: string | null;
};

export type CompleteInterventionTaskRequest = {
  notes: string | null;
};

// MW-153: the Suicide Risk Screener (IW EAP MW19). The answers are numbered as on the form; the scales are lookups
// (RISK_SCREENER_*), so a scale answer is the id of a lookup item.
export type RiskScreenerAnswers = {
  feltSadId: string | null;
  feltNoHopeId: string | null;
  feltShameOrGuiltId: string | null;
  feltWorthlessId: string | null;
  attemptedSuicide: boolean | null;
  attemptCountId: string | null;
  lastAttemptId: string | null;
  changesSinceAttempt: string | null;
  recentUpsettingEvents: boolean | null;
  eventFamilyBreakdown: boolean;
  eventSexualIdentityConflict: boolean;
  eventChildCustody: boolean;
  eventRelationshipProblem: boolean;
  eventLossOfLovedOne: boolean;
  eventChronicPainOrIllness: boolean;
  eventTrauma: boolean;
  eventImpendingLegalProsecution: boolean;
  eventOther: string | null;
  thoughtsOfSuicide: boolean | null;
  thoughtFrequency: string | null;
  thoughtDuration: string | null;
  worstThoughtIntensityId: string | null;
  recentThoughtIntensityId: string | null;
  hasPlan: boolean | null;
  planMethod: string | null;
  accessToMeans: boolean | null;
  planLocation: string | null;
  preparationsMade: boolean | null;
  planLikelihoodId: string | null;
  protectiveFactors: string | null;
  hasSupport: boolean | null;
  supportPeople: string | null;
  supportContactFrequencyId: string | null;
  pastCoping: string | null;
  presentsAgitated: boolean;
  presentsConfused: boolean;
  presentsDelusional: boolean;
  presentsOther: string | null;
};

export type RiskScreenerRequest = RiskScreenerAnswers & {
  screenedDate: string | null;
  riskRatingId: string | null;
};

export type RiskScreenerResponse = RiskScreenerAnswers & {
  id: string;
  interventionId: string;
  screenedDate: string;
  screenedByName: string | null;
  recordedDate: string;
  riskRatingId: string;
  riskRatingCode: string;
  riskRatingName: string;
};

// MW-161: the referral summary the Momentum case manager records (presenting problems and treatment goals), and the
// achieved outcome the close-out report writes against each goal.
export type PresentingProblemResponse = { id: string; sortOrder: number; description: string };

export type TreatmentGoalResponse = { id: string; sortOrder: number; goal: string; achievedOutcome: string | null };

// The member as a provider is told them: who receives the service, how to reach them, and the customer (nothing else of the member is shared).
export type ReferralMemberResponse = {
  memberType: string | null;
  name: string;
  mobileNumber: string | null;
  emailAddress: string | null;
  customer: string | null;
};

// What a provider is told of a case it has accepted: the problems and goals, why the referral was made, the primary problem and the risk rating,
// and the member (only the provider is sent the member; internal staff have the member context of the case).
export type ReferralSummaryResponse = {
  presentingProblems: PresentingProblemResponse[];
  treatmentGoals: TreatmentGoalResponse[];
  reason: string | null;
  primaryProblem: string | null;
  riskRating: string | null;
  member: ReferralMemberResponse | null;
};

export const EMPTY_REFERRAL_SUMMARY: ReferralSummaryResponse = {
  presentingProblems: [],
  treatmentGoals: [],
  reason: null,
  primaryProblem: null,
  riskRating: null,
  member: null,
};

// What is sent is what is saved: a part that is left out is not touched (the wizard sends the goals alone).
export type ReferralSummaryRequest = {
  presentingProblems?: { id: string | null; description: string }[];
  treatmentGoals?: { id: string | null; goal: string }[];
};

// What the clinician and the member prefer for the referral (recorded at the Referral stage, used to rank the affiliates)
export type ReferralPreferenceRequest = {
  counsellingTypeId: string | null;
  counsellingTypeOther: string | null;
  preferredLanguageId: string | null;
  preferredLanguageOther: string | null;
  preferredAffiliateGenderId: string | null;
  preferredAffiliateGenderOther: string | null;
  preferredAffiliateReligionId: string | null;
  preferredAffiliateReligionOther: string | null;
  preferredCounsellingMediumId: string | null;
  preferredSuburb: string | null;
  specialNeeds: { specialNeedTypeId: string; otherDetail: string | null }[];
  additionalServices: { supportServiceTypeId: string; notes: string | null }[];
};

export type ReferralPreferenceResponse = Omit<ReferralPreferenceRequest, 'specialNeeds' | 'additionalServices'> & {
  // Empty until something has been saved
  id: string | null;
  interventionId: string;
  specialNeeds: { id: string; specialNeedTypeId: string; name: string | null; code: string | null; otherDetail: string | null }[];
  additionalServices: { id: string; supportServiceTypeId: string; name: string | null; code: string | null; notes: string | null }[];
};

export type TreatmentGoalOutcomeRequest = { goalId: string; outcome: string | null };

// MW-162: the Formal EAP Counselling clinical case notes of an attended session.
export type SessionNoteType = 'First' | 'Intermediate';

export type SessionNoteImpact = { functioningAreaId: string; description: string; rating: number };

export type SessionNoteOutOfScope = { problem: string; intervention: string | null; resource: string | null };

export type SessionNoteRequest = {
  copingLevel: number | null;
  riskRatingId: string | null;
  interventionsProvided: string | null;
  homeworkGiven: string | null;
  commitmentLevel: number | null;
  clinicalImpression: string | null;
  processRisks: string | null;
  nextSteps: string | null;
  nextSessionDate: string | null;
  nextSessionReason: string | null;
  earlyTermination: boolean | null;
  additionalComments: string | null;
  motivationForAdditionalSessions: boolean | null;
  problemSeverity: number | null;
  goalsDiscussed: boolean | null;
  goalsAgreed: boolean | null;
  goalsNotDiscussedReason: string | null;
  initialSessionsRequired: number | null;
  impactAreas: SessionNoteImpact[];
  outOfScopeProblems: SessionNoteOutOfScope[];
};

export type SessionNoteResponse = Omit<SessionNoteRequest, 'impactAreas' | 'outOfScopeProblems'> & {
  id: string;
  interventionId: string;
  sessionId: string;
  sessionNumber: number;
  sessionDate: string;
  providerName: string | null;
  noteType: SessionNoteType;
  submittedDate: string;
  riskRatingId: string;
  riskRatingCode: string;
  riskRatingName: string;
  impactAreas: (SessionNoteImpact & { functioningAreaName: string })[];
  outOfScopeProblems: SessionNoteOutOfScope[];
};

// MW-188: who referred the member (kept with the intervention; internal staff only)
export type ReferrerRequest = {
  name: string | null;
  surname: string | null;
  position: string | null;
  emailAddress: string | null;
  officeNumber: string | null;
  mobileNumber: string | null;
  referrerEmployeeId: string | null;
};

export type ReferrerResponse = ReferrerRequest & {
  // Empty until a referrer has been saved
  id: string | null;
  interventionId: string;
  referrerEmployeeName: string | null;
};

// MW-188: the Eligibility step. The case keeps a snapshot of the member (and dependant) that follows the live records until eligibility passes.
export type MemberContextResponse = {
  id: string | null;
  interventionId: string;
  memberTypeCode: string | null;
  memberType: string | null;
  employeeId: string | null;
  dependantId: string | null;
  capturedDate: string | null;
  lockedDate: string | null;
  memberName: string | null;
  memberSurname: string | null;
  memberIdNumber: string | null;
  memberEmployeeNumber: string | null;
  memberDateOfBirth: string | null;
  memberGenderId: string | null;
  memberGender: string | null;
  memberMaritalStatusId: string | null;
  memberMaritalStatus: string | null;
  memberMobileNumber: string | null;
  memberEmailAddress: string | null;
  dependantType: string | null;
  dependantName: string | null;
  dependantSurname: string | null;
  dependantIdNumber: string | null;
  dependantMobileNumber: string | null;
  dependantEmailAddress: string | null;
  authorisationStatus: string | null;
  authorisationMethod: string | null;
  level1Id: string | null;
  // The customer (the client the member belongs to)
  clientName: string | null;
  level1: string | null;
  level2Id: string | null;
  level2: string | null;
  level3Id: string | null;
  level3: string | null;
  companyJobTitleId: string | null;
  companyJobTitle: string | null;
  ageYearsAtStart: number | null;
};

export type EligibilityProductResponse = { id: string; momentumProductId: string; name: string | null; code: string | null; verifiedDate: string };

export type EligibilityCheckResponse = {
  id: string;
  ruleCode: string | null;
  ruleName: string | null;
  result: 'Pass' | 'Warn' | 'Fail';
  detail: string | null;
  overrideReason: string | null;
  checkedDate: string;
};

export type EligibilityResponse = {
  interventionId: string;
  status: 'Pending' | 'Eligible' | 'NotEligible';
  locked: boolean;
  companyId: string | null;
  member: MemberContextResponse;
  products: EligibilityProductResponse[];
  checks: EligibilityCheckResponse[];
};

// A field left out (null) is not changed.
export type EligibilityMemberRequest = {
  firstName: string | null;
  surname: string | null;
  idNumber: string | null;
  dateOfBirth: string | null;
  genderId: string | null;
  maritalStatusId: string | null;
  contactNumber: string | null;
  emailAddress: string | null;
  companyJobTitleId: string | null;
  level1Id: string | null;
  level2Id: string | null;
  level3Id: string | null;
};

export type EligibilityDependantRequest = {
  dependantId: string | null;
  dependantTypeId: string | null;
  firstName: string | null;
  surname: string | null;
  idNumber: string | null;
  contactNumber: string | null;
  emailAddress: string | null;
  genderId: string | null;
  maritalStatusId: string | null;
  authorisedByMember: boolean | null;
  declineReasonId: string | null;
  declineReasonOther: string | null;
};

// MW-188: the coded presenting problem of the case: a primary and an optional secondary problem, each under its cluster.
export type CodedProblemRequest = { clusterId: string | null; problemId: string | null };

export type PresentingProblemCodingRequest = { primary: CodedProblemRequest; secondary: CodedProblemRequest };

export type CodedProblemResponse = { clusterId: string | null; cluster: string | null; problemId: string | null; problem: string | null };

export type PresentingProblemCodingResponse = { interventionId: string; primary: CodedProblemResponse; secondary: CodedProblemResponse };

// MW-188: where the case is in the intake wizard, worked out on the server from what each step saved.
export type InterventionProgressState = 'Complete' | 'InProgress' | 'NotStarted' | 'NotApplicable';

// missing says what is left in a step that is not done
export type InterventionProgressStep = { key: string; label: string; state: InterventionProgressState; missing?: string | null };

// The Live context summary beside the steps: who the case is for and where it stands. Anything not known yet is null.
export type InterventionContextSummary = {
  memberName: string | null;
  userType: string | null;
  customer: string | null;
  riskRating: string | null;
  eligibilityStatus: string;
  primaryProblem: string | null;
  selectedProvider: string | null;
};

export type InterventionProgressResponse = {
  interventionId: string;
  steps: InterventionProgressStep[];
  nextStepKey: string | null;
  context: InterventionContextSummary | null;
};
